// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Badge,
  Icon,
} from 'antd';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import getFilterOperators from 'utils/getFilterOperators';

import {
  PAGES,
  FILTERS_TYPES,
} from 'appconstants';
import {
  SpButton,
  FilterWrapper,
  SpText,
  SpStatusTag,
} from 'components/DesignKit';

import { adminOrdersActions, filterParamsActions } from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';
import moment from 'moment-timezone';

import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import formatAmount from 'utils/formatAmount';
import getCatalogOrderBackground from 'utils/getCatalogOrderBackground';
import getCatalogOrderStatus from 'utils/getCatalogOrderStatus';
import getOrderType from 'utils/getOrderType';
import UpdateOrder from './components/UpdateOrder';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const ISSUER_FILTERS_TYPES = FILTERS_TYPES.CATALOG_ORDERS;
const placeholder = 'Select type';

type Props = {
  fetchAdminOrders: Function,
  adminOrders: Array<Object>,
  loading: boolean,
  test: boolean,
  totalElements: number,
  history: {
    push: Function,
  },
  selectedAccount: Object,
  submitting: boolean,
  updateAdminOrder: Function,
  validator: boolean,
  setFilterData: Function,
  preserveFilters: Object,
  isMobileView: Boolean,
};

const Orders = (props: Props) => {
  const {
    loading,
    fetchAdminOrders,
    adminOrders,
    totalElements,
    test,
    history,
    selectedAccount,
    submitting,
    updateAdminOrder,
    validator,
    setFilterData,
    preserveFilters,
    isMobileView,
  } = props;

  const tableId = document.getElementsByTagName('table');
  if (tableId && tableId[0] && isMobileView) {
    tableId[0].style.width = '1600px';
  } else if (tableId && tableId[0] && !isMobileView) {
    tableId[0].style.width = '100%';
  }

  const location = useLocation();
  const myRef = useRef();
  const { pathname } = location;
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [addIssuerModal, setAddIssuerModal] = useState(false);
  const [filterTypes, setFilterTypes] = useState(ISSUER_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const currencyType = selectedAccount && selectedAccount.currency;
  const [filterIssuer, setFilterIssuer] = useState((preserveFilters && preserveFilters.filterIssuer)
    ? (preserveFilters && preserveFilters.filterIssuer)
    : [{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterIssuer: [{
        filterType: '',
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        filterValueFrom: '',
        filterValueTo: '',
        checked: true,
      }],
    });
  const [selectedOrder, setSelectedOrder] = useState();

  const exportData = adminOrders ? adminOrders.map((values) => ({
    id: values && values.gid.slice(-6),
    status: values && values.catalogOrderStatus,
    createdAt: moment
      .utc(values && values.createdAt)
      .tz(selectedAccount && selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
    product: values && values.catalog.name,
    qnty: values && values.quantity,
    price: values && values.catalog.price / 100,
    frequency: values && values.billingFrequency,
    type: values && values.catalogOrderType,
  })) : '';

  const exportHeaders = [
    { label: 'ID', key: 'id' },
    { label: 'STATUS', key: 'status' },
    { label: 'DATE', key: 'createdAt' },
    { label: 'PRODUCT', key: 'product' },
    { label: 'QUANTITY', key: 'qnty' },
    { label: 'PRICE', key: 'price' },
    { label: 'FREQUENCY', key: 'frequency' },
    { label: 'ORDER TYPE', key: 'type' },
  ];

  const debouncedFetchOrders = debounce(fetchAdminOrders, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchOrders({ currentPage, sortParams, filterParams });
  };

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      setOpen(false);
    }
  };

  useEffect(() => {
    getData(location.search);
  }, [test, selectedAccount, location]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [adminOrders]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  useEffect(() => {
    if (!validator) {
      setAddIssuerModal(false);
    }
  }, [validator]);

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.CATALOG_ORDERS,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.CATALOG_ORDERS,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const addNewIssuer = (data: Object) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const {
      gid,
      type,
      internalNotes,
      externalNotes,
    } = data;

    const params = {
      catalogOrderStatus: type,
      adminInternalNotes: internalNotes,
      adminExternalNotes: externalNotes,
    };

    updateAdminOrder({
      gid,
      params,
      currentPage,
      sortParams,
    });
  };

  const onCheck = (e, index) => {
    const list = [...filterIssuer];
    if (filterIssuer.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = ISSUER_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setFilterIssuer(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterIssuer];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else if (e[1] === 'amount') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'type') {
      list[index].filterType = 'Dropdown';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = ISSUER_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setFilterIssuer(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleFromValue = (e, index) => {
    const list = [...filterIssuer];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueFrom = e.currentTarget.value;
      setFilterIssuer(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueFrom = e;
      setFilterIssuer(list);
    }
  };

  const handleToValue = (e, index) => {
    const list = [...filterIssuer];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueTo = e.currentTarget.value;
      setFilterIssuer(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueTo = e;
      setFilterIssuer(list);
    }
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterIssuer[index].filterName === 'createdAt') {
      if (filterIssuer[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).startOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterIssuer[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterIssuer[index].filterOperator === 'GT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterIssuer];
    list[index].filterValue = selectedValue;
    setFilterIssuer(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterIssuer];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterIssuer[index].filterValue;
        handleValue(value, index);
      }
    }
    setFilterIssuer(list);
  };

  const handleAddClick = () => {
    const list = [...filterIssuer];
    setFilterIssuer([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setFilterIssuer([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
    setFilterTypes(ISSUER_FILTERS_TYPES);
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.CATALOG_ORDERS);
  };

  const applyFilters = () => {
    const acquirerFilters = filterIssuer.filter((item) => item.checked);
    for (let i = 0; i < acquirerFilters.length; i += 1) {
      if (!acquirerFilters[i].filterName || !acquirerFilters[i].filterOperator || !acquirerFilters[i].filterValue) {
        return;
      }
    }
    if (acquirerFilters[0].filterValue || (acquirerFilters[0].filterValueFrom && acquirerFilters[0].filterValueTo)) {
      setCount(acquirerFilters && acquirerFilters.length);
      for (let i = 0; i < acquirerFilters.length; i += 1) {
        if (acquirerFilters[i].checked) {
          if (acquirerFilters[i].filterName === 'createdAt') {
            acquirerFilters[i] = acquirerFilters[i].filterValue;
          } else if (acquirerFilters[i].filterName === 'amount' && acquirerFilters[i].filterOperator === 'BT') {
            const name = acquirerFilters[i].filterName;
            const from = acquirerFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = acquirerFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            acquirerFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (acquirerFilters[i].filterName === 'amount' && acquirerFilters[i].filterOperator !== 'BT') {
            const value = acquirerFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            acquirerFilters[i] = `${acquirerFilters[i].filterName}.${acquirerFilters[i].filterOperator}=${value}`;
          } else {
            acquirerFilters[i] = `${acquirerFilters[i].filterName}.${acquirerFilters[i].filterOperator}=${acquirerFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(acquirerFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterIssuer,
        });
        history.push({
          pathname: PAGES.CATALOG_ORDERS,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterIssuer,
        });
        history.push({
          pathname: PAGES.CATALOG_ORDERS,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const editOrder = (record) => {
    setSelectedOrder(record);
    setAddIssuerModal(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'gid',
      width: '10%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (gid) => (
        <span>
          {gid.slice(-6) || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'catalogOrderStatus',
      width: '10%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (catalogOrderStatus) => (
        <SpStatusTag style={{ backgroundColor: getCatalogOrderBackground(catalogOrderStatus) }}>
          {getCatalogOrderStatus(catalogOrderStatus)}
        </SpStatusTag>
      ),
    },
    {
      title: 'DATE',
      dataIndex: 'createdAt',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
    {
      title: 'PRODUCT',
      dataIndex: 'catalog.name',
      width: '10%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text, record) => (
        <span>
          {record.name || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      width: '10%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (quantity) => (
        <span>
          {quantity || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'PRICE',
      dataIndex: 'catalog.price',
      width: '10%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text, record) => (
        <span style={{ cursor: 'pointer' }}>
          {currencyType.prefix} {formatAmount((record.price / 100), currencyType)}
        </span>
      ),
    },
    {
      title: 'FREQUENCY',
      dataIndex: 'billingFrequency',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (billingFrequency) => (
        <span style={{ cursor: 'pointer' }}>
          {billingFrequency || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'catalogOrderType',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (catalogOrderType) => (
        <span style={{ cursor: 'pointer' }}>
          {getOrderType(catalogOrderType) || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'ACTION',
      dataIndex: 'action',
      width: '15%',
      align: 'left',
      render: (text, record) => (
        <span>
          <Tooltip placement="top" title="Edit">
            <Icon
              type="edit"
              className="ml-3"
              style={{ fontSize: '20px', cursor: 'pointer' }}
              onClick={() => editOrder(record)}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <Col>
            <span>
              {
                exportData !== null
                  ? (
                    <Tooltip placement="left" title="Click here to export Orders">
                      <CSVLink
                        target="_blank"
                        className="mr-3"
                        filename="Issuer Report.csv"
                        data={exportData}
                        headers={exportHeaders}
                        onMouseEnter={() => setFillColor(true)}
                        onMouseLeave={() => setFillColor(false)}
                      >
                        <ExportIcon
                          fill={fillColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </CSVLink>
                    </Tooltip>
                  ) : null
              }
            </span>
            <span ref={myRef}>
              <span
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setFilterColor(true)}
                onMouseLeave={() => setFilterColor(false)}
              >
                {open && (
                  <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                    <FilterIcon
                      fill={filterColor
                        ? '#279dfe'
                        : '#C0C0C0'}
                    />
                  </Badge>
                )}
                {!open && (
                  <Tooltip placement="left" title="Click here to filter Orders">
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  </Tooltip>
                )}
              </span>
              {open && (
                <FilterWrapper
                  style={{ marginLeft: '-263px' }}
                >
                  <Filters
                    filtersGrid={filterIssuer}
                    filterTypes={filterTypes}
                    handleChange={handleChange}
                    onCheck={onCheck}
                    fiterOperators={fiterOperators}
                    handleOperator={handleOperator}
                    handleValue={handleValue}
                    startDate={startDate}
                    endDate={endDate}
                    currentDate={currentDate}
                    handleFromValue={handleFromValue}
                    handleToValue={handleToValue}
                    placeholder={placeholder}
                  />
                  <div className="px-4 mt-2">
                    {
                      filterIssuer.length < ISSUER_FILTERS_TYPES.length && (
                        <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={handleAddClick}
                          >
                            +&nbsp; Add another filter
                          </SpText>
                        </div>
                      )
                    }
                  </div>
                  <div className="d-flex mb-2 justify-content-end mr-3">
                    <SpButton
                      type="secondary"
                      shape="round"
                      className="mr-2"
                      ghost
                      onClick={resetFilters}
                    >
                      Clear
                    </SpButton>
                    <SpButton
                      type="primary"
                      shape="round"
                      onClick={applyFilters}
                    >
                      Save
                    </SpButton>
                  </div>
                </FilterWrapper>
              )}
            </span>
          </Col>
        </div>
      </Row>

      {/* ADD ISSUER MODAL */}
      {addIssuerModal && (
        <UpdateOrder
          visible={addIssuerModal}
          submitting={submitting}
          selectedAccount={selectedAccount}
          selectedOrder={selectedOrder}
          close={() => setAddIssuerModal(false)}
          submit={addNewIssuer}
        />
      )}

      <Table
        rowClassName={() => 'ant-table-clickable-row'}
        columns={columns}
        rowKey={(record) => record.gid}
        dataSource={adminOrders}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  adminOrders: state.adminOrders.content,
  loading: state.loading.loading,
  test: state.account.test,
  totalElements: state.adminOrders.totalElements,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
  preserveFilters: state.filterParams.preserveFilters,
  isMobileView: state.mobileView.isMobileView,
});

const mapDispatchToProps = (dispatch) => ({
  fetchAdminOrders: param => dispatch(adminOrdersActions.fetchAdminOrders({
    payload: param,
  })),
  updateAdminOrder: param => dispatch(adminOrdersActions.updateAdminOrder({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(Orders);
